const DEFAULT_COMPANY_COLORS = [
  '#1677ff',
  '#13c2c2',
  '#52c41a',
  '#fa8c16',
  '#eb2f96',
  '#722ed1',
  '#2f54eb',
  '#fa541c',
];

const ADJACENT_POLYGON_Y_GAP = 0.025;

export function createCompanyColorMap(companies) {
  return companies.reduce((accumulator, company, index) => {
    accumulator[company] =
      DEFAULT_COMPANY_COLORS[index % DEFAULT_COMPANY_COLORS.length];
    return accumulator;
  }, {});
}

function hexToRgba(hexColor, alpha) {
  const normalized = hexColor.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

// ── polygon 경로 생성 ─────────────────────────────────────────────────────
function tracePolygon(ctx, vertices, width, height) {
  if (!vertices || vertices.length < 3) return false;
  ctx.beginPath();
  tracePolygonSubpath(ctx, vertices, width, height);
  return true;
}

function tracePolygonSubpath(ctx, vertices, width, height) {
  ctx.moveTo(vertices[0].x * width, vertices[0].y * height);
  for (let i = 1; i < vertices.length; i++) {
    ctx.lineTo(vertices[i].x * width, vertices[i].y * height);
  }
  ctx.closePath();
}

function getRowPolygons(row) {
  if (Array.isArray(row?.normalizedPolygons) && row.normalizedPolygons.length) {
    return row.normalizedPolygons.filter((polygon) => polygon?.length >= 3);
  }
  if (row?.normalizedVertices?.length >= 3) {
    return [row.normalizedVertices];
  }
  return [];
}

function isPointInsidePolygon(vertices, nx, ny) {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i].x;
    const yi = vertices[i].y;
    const xj = vertices[j].x;
    const yj = vertices[j].y;
    const intersect =
      yi > ny !== yj > ny && nx < ((xj - xi) * (ny - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function getPolygonMetrics(vertices) {
  if (!Array.isArray(vertices) || vertices.length < 3) return null;
  const xs = vertices.map((vertex) => vertex.x);
  const ys = vertices.map((vertex) => vertex.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
    centerY: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

function buildSelectedPolygonRecords(rows, selectedRowIds, filterCompanyName) {
  const byCompany = new Map();

  rows.forEach((row) => {
    if (!selectedRowIds.has(row.rowId)) return;
    const isFilteredOut =
      filterCompanyName && row.rawPharmaCompanyName !== filterCompanyName;
    if (isFilteredOut) return;

    const company = row.rawPharmaCompanyName || '미분류';
    const records = getRowPolygons(row)
      .map((vertices) => {
        const metrics = getPolygonMetrics(vertices);
        if (!metrics) return null;
        return {
          rowId: row.rowId,
          vertices,
          ...metrics,
        };
      })
      .filter(Boolean);

    if (!records.length) return;
    const current = byCompany.get(company) || [];
    current.push(...records);
    byCompany.set(company, current);
  });

  return byCompany;
}

function groupAdjacentPolygonRecords(records) {
  if (!records.length) return [];

  const sorted = [...records].sort(
    (left, right) => left.centerY - right.centerY,
  );
  const groups = [[sorted[0]]];

  for (let index = 1; index < sorted.length; index += 1) {
    const polygon = sorted[index];
    const currentGroup = groups[groups.length - 1];
    const previousPolygon = currentGroup[currentGroup.length - 1];
    const yGap = polygon.minY - previousPolygon.maxY;

    if (yGap < ADJACENT_POLYGON_Y_GAP) {
      currentGroup.push(polygon);
      continue;
    }

    groups.push([polygon]);
  }

  return groups;
}

function traceMergedPolygonGroup(ctx, polygonGroup, width, height) {
  if (!polygonGroup.length) return false;

  if (polygonGroup.length === 1) {
    return tracePolygon(ctx, polygonGroup[0].vertices, width, height);
  }

  ctx.beginPath();
  polygonGroup.forEach((polygon) => {
    tracePolygonSubpath(ctx, polygon.vertices, width, height);
  });
  return true;
}

// ── point-in-polygon (ray casting) ───────────────────────────────────────
export function isPointInsideRowBounds(row, nx, ny) {
  const polygons = getRowPolygons(row);
  if (!polygons.length) {
    // fallback to bounds rect
    const b = row?.bounds;
    if (!b) return false;
    return nx >= b.minX && nx <= b.maxX && ny >= b.minY && ny <= b.maxY;
  }

  return polygons.some((polygon) => isPointInsidePolygon(polygon, nx, ny));
}

function getRowBounds(row) {
  const polygons = getRowPolygons(row);
  if (polygons.length) {
    const verts = polygons.flat();
    return {
      minX: Math.min(...verts.map((v) => v.x)),
      maxX: Math.max(...verts.map((v) => v.x)),
      minY: Math.min(...verts.map((v) => v.y)),
      maxY: Math.max(...verts.map((v) => v.y)),
    };
  }
  return row?.bounds ?? null;
}

export function drawRowHighlights({
  context,
  rows,
  selectedRowIds,
  companyColors,
  width,
  height,
  filterCompanyName = null,
}) {
  context.save();
  context.lineJoin = 'round';

  // Pass 1: dashed outlines for unselected / filtered-out rows
  rows.forEach((row) => {
    const isSelected = selectedRowIds.has(row.rowId);
    const isFilteredOut =
      filterCompanyName && row.rawPharmaCompanyName !== filterCompanyName;
    if (isSelected && !isFilteredOut) return;

    const color = companyColors[row.rawPharmaCompanyName] || '#1677ff';
    getRowPolygons(row).forEach((polygon) => {
      if (!tracePolygon(context, polygon, width, height)) return;
      context.strokeStyle = hexToRgba(color, 0.18);
      context.lineWidth = 1;
      context.setLineDash([5, 4]);
      context.stroke();
    });
  });

  // Pass 2: merge adjacent selected polygons per company into one filled path.
  const byCompany = buildSelectedPolygonRecords(
    rows,
    selectedRowIds,
    filterCompanyName,
  );

  byCompany.forEach((polygonRecords, company) => {
    const color = companyColors[company] || '#1677ff';
    const groups = groupAdjacentPolygonRecords(polygonRecords);

    groups.forEach((group) => {
      if (!traceMergedPolygonGroup(context, group, width, height)) return;
      context.setLineDash([]);
      context.fillStyle = hexToRgba(color, 0.16);
      context.fill();
      context.strokeStyle = hexToRgba(color, 0.45);
      context.lineWidth = 1;
      context.stroke();
    });
  });

  context.restore();
}
